/* eslint-disable max-len */
import { emailRegex } from '@aarhus-university/au-lib-react-components/src/lib/helpers';
import {
  getDetailsCount,
  PRIORITY_MAX,
} from './helpers';
import placeOffers from './placeoffer';

export const getDataLayerForViewApplicationsOverview = (
  applicationsFromStore: IApplication[],
  applicant: IApplicant | null,
  phase: number,

): IDataLayerForViewApplicationsOverview => {
  const applications = applicationsFromStore || [];
  const [placeOfferShowing, , , , areAllNotOffered, offeredNotRejected] = placeOffers(applications, phase);
  let response: IApplicationResponse | null = null;
  if ((offeredNotRejected as IApplication[]).length) {
    response = (offeredNotRejected as IApplication[])[0].response;
  }

  let placeOfferType = '';
  let placeOfferStart = '';
  if (areAllNotOffered) {
    placeOfferType = 'NoOffer';
  }
  if (response) {
    if (response.standby) {
      placeOfferType = 'Standby';
      if (response.conditional) {
        placeOfferType = 'StandbyConditional';
      }
    } else if (response.conditional && !response.standby) {
      placeOfferType = 'Conditional';
    } else {
      placeOfferType = 'Ordinary';
    }
    if ((offeredNotRejected as IApplication[]).length > 1) {
      placeOfferType = 'Multiple';
    }

    if (response.offeredDelayed) {
      placeOfferStart = 'Winter';
    } else {
      placeOfferStart = 'Summer';
    }
  }

  const vacant = applications.filter((a) => !a.response.cancelled && a.priority > PRIORITY_MAX).length;
  const active = applications.filter((a) => !a.response.cancelled && a.priority <= PRIORITY_MAX).length;
  const cancelled = applications.filter((a) => a.response.cancelled).length;

  const dataLayer: IDataLayerForViewApplicationsOverview = {
    applications: {
      lists: {
        vacant,
        active,
        cancelled,
        total: vacant + active + cancelled,
      },
      status: {
        pending: applications.filter((a) => !a.response.cancelled && a.response.offered == null && a.response.requirementsFulfilled == null).length,
        fulfilled: applications.filter((a) => !a.response.cancelled && a.response.offered == null && a.response.requirementsFulfilled).length,
        fulfilledConditional: applications.filter((a) => !a.response.cancelled && a.response.offered == null && a.response.conditional).length,
        notFulfilled: applications.filter((a) => !a.response.cancelled && a.response.offered == null && a.response.requirementsFulfilled === false).length,
        placeOffered: applications.filter((a) => !a.response.cancelled && a.response.offered && a.response.offerAccepted == null && !a.response.standby).length,
        placeOfferedConditional: applications.filter((a) => !a.response.cancelled && a.response.offered && a.response.conditional).length,
        standby: applications.filter((a) => !a.response.cancelled && a.response.offered && a.response.standby).length,
        standbyConditional: applications.filter((a) => !a.response.cancelled && a.response.offered && (a.response.standby && a.response.conditional)).length,
        placeNotOffered: applications.filter((a) => !a.response.cancelled && a.response.offered === false).length,
        offerAccepted: applications.filter((a) => a.response.offered && a.response.offerAccepted).length,
        offerRejected: applications.filter((a) => a.response.offered && a.response.offerAccepted === false).length,
        cancelled: applications.filter((a) => a.response.cancelled && a.response.cancelled).length,
      },
      quota: {
        quota1: applications.filter((a) => !a.response.cancelled && a.request.quota1 && !a.request.quota2).length,
        quota2: applications.filter((a) => !a.response.cancelled && a.request.quota2 && !a.request.quota1).length,
        quota1and2: applications.filter((a) => !a.response.cancelled && a.request.quota1 && a.request.quota2).length,
      },
      details: {
        count: {
          count0: applications.filter((a) => !a.response.cancelled && getDetailsCount(a) === 0).length,
          count1: applications.filter((a) => !a.response.cancelled && getDetailsCount(a) === 1).length,
          count2: applications.filter((a) => !a.response.cancelled && getDetailsCount(a) === 2).length,
          count3: applications.filter((a) => !a.response.cancelled && getDetailsCount(a) === 3).length,
          count4: applications.filter((a) => !a.response.cancelled && getDetailsCount(a) === 4).length,
        },
        type: {
          quota1Recalculated: applications.filter((a) => !a.response.cancelled && a.response.quota1RecalculatedQuotient).length,
          quota2: applications.filter((a) => !a.response.cancelled && a.response.quota2Quotient).length,
          winter: applications.filter((a) => !a.response.cancelled && a.request.delayed).length,
          standby: applications.filter((a) => !a.response.cancelled && a.request.standby).length,
        },
      },
    },
    applicant: null,
    placeOffer: {
      shown: placeOfferShowing as boolean,
      type: !placeOfferType || !placeOfferShowing ? null : placeOfferType,
      start: !placeOfferStart || !placeOfferShowing ? null : placeOfferStart,
    },
    emptyStates: {
      noApplications: applications.length === 0,
      noExam: !applicant?.basisForAdmission,
      noEmail: !applicant?.email,
      noCourses: applicant ? applicant?.courses?.length === 0 : true,
      notApplicant: applicant?.auId === 0,
      portalClosed: phase === 0,
    },
  };

  if (applicant != null && applicant?.auId !== 0) {
    dataLayer.applicant = {
      quota2Applicant: !!applications.find((a) => !a.response.cancelled && a.request.quota2Applicant),
      nationality: applicant.nationality,
      multipleGPAs: !!applications.find((a) => !a.response.cancelled && (a.response.quota2Quotient || a.response.quota1RecalculatedQuotient)),
      exam: applicant.examCode,
      courses: {
        total: phase === 1 ? 0 : applicant.courses.length,
        failed: phase === 1 ? 0 : applicant.courses.filter((c) => c.state === 'Failed').length,
        conditional: phase === 1 ? 0 : applicant.courses.filter((c) => c.state === 'Conditional').length,
        exemption: phase === 1 ? 0 : applicant?.courses.filter((c) => c.state === 'Exemption').length,
        passed: phase === 1 ? 0 : applicant.courses.filter((c) => c.state === 'Passed').length,
      },
    };
  }

  return dataLayer;
};

export const getDataLayerForRespondToOffer = (application: IApplication): IDataLayerForRespondToOffer => {
  const { response: singleResponse } = application;
  let type = 'ResponseToOrdinary';
  if (singleResponse.conditional) {
    type = 'ResponseToConditional';
  }
  if (singleResponse.standby) {
    type = 'ResponseToOrdinaryWithStandby';
  }
  if (singleResponse.standby && singleResponse.conditional) {
    type = 'ResponseToConditionalWithStandby';
  }
  return {
    response: singleResponse.offerAccepted ? 'Accept' : 'Reject',
    type,
    start: singleResponse.offeredDelayed ? 'Winter' : 'Summer',
    areaCode: application.admissionAreaCode,
  };
};

export const getDataLayerForReceipt = (
  application: IApplication,
  applicant: IApplicant,
): IDataLayerForReceipt => {
  let hasConditionalInfo = false;
  if (application) {
    const { response: { conditional, standby } } = application;
    if (conditional || (standby && conditional)) {
      hasConditionalInfo = true;
    }
  }

  return {
    state: application.response.offerAccepted ? 'Accepted' : 'Rejected',
    hasConditionalInfo,
    emptyStateNoEmailOnReceipt: !applicant.email?.match(emailRegex),
  };
};

export const getDataLayerForVirtualPageview = (
  pageUrl: string,
  pageTitle: string,
  lang: string,
): IDataLayerForVirtualPageview => ({
  pageUrl,
  pageTitle,
  uiLanguage: lang,
});

export const pushAcceptRejectToGTM = (application: IApplication): void => {
  const event = 'RespondToOffer';
  console.log(`GTM: ${event}`);
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...{ event },
    ...getDataLayerForRespondToOffer(
      application,
    ),
  });
};
